import { Component, OnInit } from '@angular/core';
import { SessionService } from '../../services/session-service.service';
import { ClientService } from '../client.service';
import { ClientFormItem } from '../../components/name-entry/name-entry.component';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
} from '@angular/material/legacy-dialog';
import { ErrorDialogComponent } from '../../components/shared/error-dialog/error-dialog.component';
import { BehaviorSubject } from 'rxjs';
import { AddressFormItem } from '../../components/address-lookup/address-lookup.component';
import { Country } from '@appyvet/vetbooker-definitions/dist/countries';
import { AddressRequirements } from '@appyvet/vetbooker-definitions/dist/client_patient_details';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { PMS } from '../../enums/pms-name.enum';
import { AddressConfigService } from '../../components/address-lookup/address-config.service';
import { PMSAddressConfig } from '../../components/address-lookup/address-config.interface';
import { ExtendedAddressObject } from '../../interfaces/extended-address.interface';
import { AlertDialogComponent } from '../../components/shared/alert-dialog/alert-dialog.component';

@Component({
  selector: 'app-client-details',
  templateUrl: './client-details.component.html',
  styleUrls: ['./client-details.component.scss'],
})
export class ClientDetailsComponent implements OnInit {
  formattedAddress: string;
  isGroomRoom: boolean;
  addressUpdate: boolean;
  canEditClient: boolean;
  nameUpdate: boolean;
  formattedName: string;
  clinicCode: string;
  saving$ = new BehaviorSubject<boolean>(false);
  addressRequirements: AddressRequirements;
  country: Country;
  currentAddress: ExtendedAddressObject;
  private clinicName: string;
  showAddressForm: boolean;
  currentPMS: string;
  currentPMSConfig: PMSAddressConfig;

  constructor(
    private sessionService: SessionService,
    public clientService: ClientService,
    private dialog: MatDialog,
    private gaService: GoogleAnalyticsService,
    private router: Router,
    private authService: AuthService,
    private addressConfigService: AddressConfigService,
  ) {}

  ngOnInit(): void {
    if (
      [PMS.ezyVet.toString(), PMS.VetIT.toString()].includes(
        this.authService.getPmsName(),
      )
    ) {
      this.router.navigate(['/']);
    }

    this.currentPMS = this.authService.getPmsName();
    this.currentPMSConfig = this.addressConfigService.getAddressConfig(
      this.currentPMS,
    );

    this.sessionService.clientPatientDetails$.subscribe(
      (clientPatientDetails) => {
        this.country = clientPatientDetails.country;
        this.addressRequirements =
          clientPatientDetails.editSettings.addressRequirements;
        this.isGroomRoom = clientPatientDetails.isGroomRoom;
        this.canEditClient = clientPatientDetails.editSettings.editClient;
        this.clinicCode = clientPatientDetails.clinicCode;
        this.clinicName = clientPatientDetails.themeSettings.customName;
        this.showAddressForm =
          clientPatientDetails.editSettings.manualAddressEntry;

        // Map client data to dynamic address structure based on PMS
        this.currentAddress = this.mapClientDataToAddress(
          clientPatientDetails.clientData.client,
        );
      },
    );

    this.clientService.client$.subscribe((client) => {
      this.formattedAddress = this.buildFormattedAddress(client);
      this.formattedName = this.buildFormattedName(client);
      this.currentAddress = this.mapClientDataToAddress(client);
    });
  }

  onSaveName(clientForm: ClientFormItem) {
    if (!clientForm.firstLoad) {
      this.saving$.next(true);
      this.clientService.updateName$(clientForm.client).subscribe(
        () => {
          this.gaService.event('Edit Name', 'Client Details', this.clinicName);
          this.nameUpdate = false;
          this.saving$.next(false);
        },
        (error) => {
          this.saving$.next(false);
          const errorDialogConfig = new MatDialogConfig();
          errorDialogConfig.data = {
            title: 'Error saving name update',
            error,
          };
          this.dialog.open(ErrorDialogComponent, errorDialogConfig);
        },
      );
    }
  }

  onSaveAddress(addressForm: AddressFormItem) {
    if (!addressForm.firstLoad) {
      // Create a plain object to ensure all properties are serialized
      const addressUpdate: { [key: string]: any } = {};

      // First, add all standard AddressObject properties
      const standardFields = [
        'address1',
        'address2',
        'address3',
        'town',
        'county',
        'postcode',
        'fullAddress',
        'city',
      ];
      standardFields.forEach((field) => {
        if (addressForm.address[field]) {
          addressUpdate[field] = addressForm.address[field];
        }
      });

      // Then, explicitly add ALL form field values to ensure PMS-specific fields are included
      this.currentPMSConfig.fields.forEach((field) => {
        const value = addressForm.form.get(field.key)?.value;
        if (value !== null && value !== undefined && value !== '') {
          addressUpdate[field.key] = value;
        }
      });

      // Also include any existing PMS-specific fields from the address object
      Object.keys(addressForm.address).forEach((key) => {
        if (!standardFields.includes(key) && addressForm.address[key]) {
          addressUpdate[key] = addressForm.address[key];
        }
      });

      // Send as plain object with type assertion to bypass TypeScript strict typing
      this.clientService.updateAddress$(addressUpdate as any).subscribe(
        (result) => {
          this.gaService.event(
            'Edit Address',
            'Client Details',
            this.clinicName,
          );
          if (result) {
            this.addressUpdate = false;
            this.sessionService.getClientPatientDetails();
          }
        },
        (error) => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              html: `
              <h3>Unable to save address</h3>
            `,
            },
          });
        },
      );
    }
  }

  onAddressCancel() {
    this.addressUpdate = false;
  }

  private mapClientDataToAddress(client: any): ExtendedAddressObject {
    // Create enhanced address object that includes PMS-specific fields
    const addressData: any = {};

    // Extract all possible address-related fields from client data
    this.currentPMSConfig.fields.forEach((field) => {
      let value = '';

      // Try to get value directly from client using the field key first
      if (client.hasOwnProperty(field.key)) {
        value = client[field.key];
      } else {
        // For special PMS fields, try alternative lookups
        if (field.key === 'nameNo') {
          // Look for nameNo in various possible locations
          value = client.nameNo || '';
        } else if (field.key === 'hNameNo') {
          value = client.hNameNo || '';
        } else if (field.key === 'address3') {
          // For address3 (town field in Merlin), look in town/city/address3
          value = client.town || client.city || client.address3 || '';
        } else {
          // Fallback to mapped property
          const clientProperty = this.mapFieldKeyToClientProperty(field.key);
          value = client[clientProperty] || '';
        }
      }

      if (value) {
        // Store the exact field key for PMS-specific fields
        addressData[field.key] = value;
      }
    });

    // Return enhanced AddressObject with PMS-specific fields
    const result = {
      address1: addressData.address1 || client.address1 || '',
      address2: addressData.address2 || client.address2 || '',
      county: addressData.state || client.state || client.county || '', // State maps to county in AddressObject
      postcode:
        addressData.postcode ||
        client.postcode ||
        client.zipCode ||
        client.zipcode ||
        '',
      town:
        addressData.address3 ||
        client.address3 ||
        client.town ||
        client.city ||
        client.suburb ||
        '',
      // Add ALL PMS-specific fields as additional properties
      ...addressData,
    } as any;

    return result;
  }

  private buildFormattedAddress(client: any): string {
    const addressParts: string[] = [];

    // Use dynamic field configuration to build formatted address
    this.currentPMSConfig.fields.forEach((field) => {
      let value = '';

      // First try to get value using the exact field key
      if (client.hasOwnProperty(field.key)) {
        value = client[field.key];
      } else {
        // Then try the mapped property
        const mappedProperty = this.mapFieldKeyToClientProperty(field.key);
        value = client[mappedProperty];
      }

      if (value) {
        addressParts.push(value);
      }
    });

    // Fallback to standard fields if dynamic config doesn't work
    if (addressParts.length === 0) {
      if (client.address1) addressParts.push(client.address1);
      if (client.address2) addressParts.push(client.address2);
      if (client.address3) addressParts.push(client.address3);
      if (client.state) addressParts.push(client.state);
      if (client.postcode) addressParts.push(client.postcode);
    }

    return addressParts.join(', ');
  }

  private buildFormattedName(client: any): string {
    let formattedName = '';
    if (client.title) {
      formattedName += client.title + ' ';
    }
    if (client.firstName) {
      formattedName += client.firstName + ' ';
    }
    if (client.surname) {
      formattedName += client.surname;
    }
    return formattedName.trim();
  }

  private mapFieldKeyToClientProperty(fieldKey: string): string {
    // Map PMS-specific field keys to client object properties
    // IMPORTANT: For unique PMS fields like nameNo, hNameNo, keep them as separate fields
    const fieldMapping: { [key: string]: string } = {
      // PMS-specific unique fields - keep as separate properties
      nameNo: 'nameNo', // Merlin - keep as separate field
      hNameNo: 'hNameNo', // Teleos - keep as separate field

      // Standard address line mappings
      address: 'address1',
      address1: 'address1',
      addressLine1: 'address1',
      streetAddress: 'address1',
      streetNumber: 'address1',
      houseNumber: 'address1',

      // Second line mappings
      street: 'address2',
      address2: 'address2',
      addressLine2: 'address2',
      secondLine: 'address2',
      apartment: 'address2',
      streetName: 'address2',
      streetAddress2: 'address2',

      // Third line / locality mappings
      locality: 'address3',
      address3: 'address3',
      thirdLine: 'address3',
      town: 'address3',
      city: 'address3',
      suburb: 'address3',
      municipality: 'address3',

      // State/county mappings
      county: 'state',
      stateProvince: 'state',
      state: 'state',
      region: 'state',

      // Postcode mappings
      postcode: 'postcode',
      postCode: 'postcode',
      postalCode: 'postcode',
      zipCode: 'postcode',
      zipcode: 'postcode',
      zipPostal: 'postcode',
    };

    return fieldMapping[fieldKey] || fieldKey;
  }
}
